import React, { useEffect, useContext, useState } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Box,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import DataTable from '../../dataTable/DataTable/DataTable';
import { EcrDeploymentsContext } from '../contexts/EcrDeploymentsContext';
import CreateAssignmentModal from '../components/CreateAssignmentModal';
import TriggerDeploymentsModal from '../components/TriggerDeploymentsModal';
import { MachineEcrDeployment } from '../model';

const useStyles = makeStyles({
  header: {
    marginBottom: 24,
  },
  filterBar: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
  },
  truncatedText: {
    display: '-webkit-box',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
  },
});

const SERVICE_NAMES = [
  'All',
  'autobulk-api',
  'autobulk-ui',
  'autobulk-database',
  'autobulk-gateway',
  'autobulk-deployment',
  'autobulk-pyplc',
];

const MachineAssignments: React.FC = () => {
  const classes = useStyles();
  const { machineDeployments, loading, fetchMachineDeployments, cancelMachineDeployment } =
    useContext(EcrDeploymentsContext);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState<string>('');
  const [selectedDeployment, setSelectedDeployment] = useState<MachineEcrDeployment | null>(null);

  const [filters, setFilters] = useState({
    service_name: 'All',
    status: 'All',
    machine_serial: '',
  });

  useEffect(() => {
    loadDeployments();
  }, []);

  const loadDeployments = () => {
    const apiFilters: any = {};
    if (filters.service_name !== 'All') apiFilters.service_name = filters.service_name;
    if (filters.status !== 'All') apiFilters.status = filters.status;
    if (filters.machine_serial) apiFilters.machine_serial = filters.machine_serial;

    fetchMachineDeployments(apiFilters);
  };

  const handleCancelClick = (deployment: MachineEcrDeployment) => {
    setSelectedDeployment(deployment);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (selectedDeployment) {
      try {
        await cancelMachineDeployment(selectedDeployment.machine_ecr_deployment_id);
        setCancelDialogOpen(false);
        setSelectedDeployment(null);
      } catch (error) {
        console.error('Error cancelling deployment:', error);
      }
    }
  };

  const getStatusChip = (deployment: MachineEcrDeployment) => {
    switch (deployment.status) {
      case 'success':
        return (
          <Chip 
            label="Success" 
            size="small" 
            sx={{ 
              backgroundColor: '#d4edda',
              color: '#155724',
              fontWeight: 500
            }} 
          />
        );
      case 'failed':
        return <Chip label="Failed" color="error" size="small" />;
      case 'cancelled':
        return <Chip label="Cancelled" color="default" size="small" />;
      case 'started':
        return (
          <Chip 
            label="Started" 
            size="small" 
            sx={{ 
              backgroundColor: '#cfe2ff',
              color: '#084298',
              fontWeight: 500
            }} 
          />
        );
      case 'received':
        return (
          <Chip 
            label="Received" 
            size="small" 
            sx={{ 
              backgroundColor: '#fff3cd',
              color: '#856404',
              fontWeight: 500
            }} 
          />
        );
      case 'pending':
      default:
        return <Chip label="Pending" color="warning" size="small" />;
    }
  };

  const handleResponseClick = (response: string) => {
    setSelectedResponse(response);
    setResponseDialogOpen(true);
  };

  const renderResponseCell = (row: MachineEcrDeployment) => {
    const response = row.machine_update_response || 'Waiting...';
    // Approximate check: if text is long enough, it likely needs truncation
    const needsTruncation = response.length > 200; // Adjust this threshold as needed

    return (
      <Box display="flex" alignItems="flex-start" gap={1}>
        <Box className={classes.truncatedText} flex={1}>
          {response}
        </Box>
        {needsTruncation && (
          <Tooltip title="Click to view full response" arrow placement="top">
            <InfoIcon 
              fontSize="small" 
              color="primary" 
              onClick={() => handleResponseClick(response)}
              sx={{ 
                cursor: 'pointer', 
                flexShrink: 0,
                marginTop: '2px',
                '&:hover': {
                  color: 'primary.dark',
                }
              }} 
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  const columns = [
    {
      name: 'Machine',
      selector: (row: MachineEcrDeployment) => row.machine_serial,
      sortable: true,
      width: '140px',
    },
    {
      name: 'Service',
      selector: (row: MachineEcrDeployment) => row.service_name,
      sortable: true,
      width: '180px',
    },
    {
      name: 'Version',
      selector: (row: MachineEcrDeployment) => row.deployment_version,
      sortable: true,
      width: '180px',
    },
    {
      name: 'Status',
      cell: (row: MachineEcrDeployment) => getStatusChip(row),
      width: '120px',
    },
    {
      name: 'Assigned',
      selector: (row: MachineEcrDeployment) => new Date(row.assigned_at).toLocaleString(),
      sortable: true,
      width: '180px',
    },
    {
      name: 'Assigned By',
      selector: (row: MachineEcrDeployment) => row.assigned_by_email || 'N/A',
      sortable: true,
      width: '200px',
    },
    {
      name: 'Response',
      cell: (row: MachineEcrDeployment) => renderResponseCell(row),
      grow: 1,
    },
    {
      name: 'Actions',
      cell: (row: MachineEcrDeployment) => (
        <Box>
          {row.status === 'pending' && (
            <Button
              variant="outlined"
              size="medium"
              startIcon={<CancelIcon />}
              onClick={() => handleCancelClick(row)}
              sx={{
                minWidth: '110px',
                fontSize: '1rem',
                fontWeight: 600,
                color: '#dc3545',
                borderColor: '#dc3545',
                '&:hover': {
                  borderColor: '#c82333',
                  backgroundColor: 'rgba(220, 53, 69, 0.04)',
                }
              }}
            >
              Cancel
            </Button>
          )}
        </Box>
      ),
      width: '140px',
      right: true,
    },
  ];

  return (
    <>
      <Box className={classes.header}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h1" component="h1">
              Machine Assignments
            </Typography>
            <Typography variant="body1" color="textSecondary" style={{ marginTop: 8 }}>
              Manage deployment assignments for machines
            </Typography>
          </Grid>
          <Grid item>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RocketLaunchIcon />}
                onClick={() => setTriggerModalOpen(true)}
              >
                Trigger Deployments
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setCreateModalOpen(true)}
              >
                Create Assignment
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Filters */}
      <Card className={classes.filterBar}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Machine Serial"
              size="small"
              value={filters.machine_serial}
              onChange={(e) => setFilters({ ...filters, machine_serial: e.target.value })}
              placeholder="Filter by serial..."
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              select
              label="Service"
              size="small"
              value={filters.service_name}
              onChange={(e) => setFilters({ ...filters, service_name: e.target.value })}
            >
              {SERVICE_NAMES.map((service) => (
                <MenuItem key={service} value={service}>
                  {service}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              select
              label="Status"
              size="small"
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="received">Received</MenuItem>
              <MenuItem value="started">Started</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadDeployments}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          <DataTable
            columns={columns}
            data={machineDeployments}
            defaultSortField="assigned_at"
            defaultSortAsc={false}
            striped
            highlightOnHover
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            progressPending={loading}
          />
        </CardContent>
      </Card>

      {/* Create Assignment Modal */}
      <CreateAssignmentModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={loadDeployments}
      />

      {/* Trigger Deployments Modal */}
      <TriggerDeploymentsModal
        open={triggerModalOpen}
        onClose={() => setTriggerModalOpen(false)}
      />

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Deployment?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this deployment assignment?
          </Typography>
          {selectedDeployment && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Machine:</strong> {selectedDeployment.machine_serial}
              </Typography>
              <Typography variant="body2">
                <strong>Service:</strong> {selectedDeployment.service_name}
              </Typography>
              <Typography variant="body2">
                <strong>Version:</strong> {selectedDeployment.deployment_version}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Note: This will set the assignment as inactive. Completed deployments cannot be cancelled.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>No, Keep It</Button>
          <Button onClick={handleCancelConfirm} color="error" variant="contained">
            Yes, Cancel Deployment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Response Details Dialog */}
      <Dialog 
        open={responseDialogOpen} 
        onClose={() => setResponseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Deployment Response</DialogTitle>
        <DialogContent>
          <Box 
            sx={{ 
              mt: 1, 
              p: 3, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '1.125rem',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '500px',
              overflowY: 'auto'
            }}
          >
            {selectedResponse}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MachineAssignments;
